/**
 * Sparkline chart (bins histogram or top values stacked bar).
 *
 * Rendered inline in ~150 column headers, so kept minimal: a single SVG element.
 */

import React from 'react';
import { useTheme } from '@fti/design-system';
import type { SparklineProps } from '../contracts';

export const Sparkline = React.memo(function Sparkline({ spark, width, height }: SparklineProps) {
  const { theme } = useTheme();

  if (spark === null) return null;

  if (spark.kind === 'bins') {
    const { bins } = spark;
    const maxCount = Math.max(...bins.map((b) => b.count), 1);
    const barWidth = width / bins.length;
    const gap = 1;
    const effectiveBarWidth = Math.max(barWidth - gap, 1);

    const title = `Distribution: ${bins.length} bins, max ${maxCount}`;

    return (
      <svg width={width} height={height} aria-hidden="true">
        <title>{title}</title>
        {bins.map((bin, i) => {
          const barHeight = bin.count === 0 ? 1 : (bin.count / maxCount) * height;
          const fill = bin.count === 0 ? theme.colors.neutral[200] : theme.colors.primary[400];
          return (
            <rect
              key={i}
              x={i * barWidth}
              y={height - barHeight}
              width={effectiveBarWidth}
              height={barHeight}
              fill={fill}
            />
          );
        })}
      </svg>
    );
  }

  if (spark.kind === 'top') {
    const { top, total } = spark;
    let x = 0;
    const colors = [
      theme.colors.primary[600],
      theme.colors.primary[300],
      theme.colors.primary[200],
    ];

    const segments: Array<{ x: number; width: number; color: string; label: string }> = [];

    // Top values with alternating colors
    top.forEach((item, i) => {
      const pct = (item.count / total) * 100;
      const segmentWidth = (item.count / total) * width;
      segments.push({
        x,
        width: segmentWidth,
        color: colors[i % colors.length],
        label: `${item.value}: ${item.count} (${pct.toFixed(1)}%)`,
      });
      x += segmentWidth;
    });

    // Remaining share
    const topTotal = top.reduce((sum, item) => sum + item.count, 0);
    if (topTotal < total) {
      const remaining = total - topTotal;
      const pct = (remaining / total) * 100;
      const segmentWidth = (remaining / total) * width;
      segments.push({
        x,
        width: segmentWidth,
        color: theme.colors.neutral[200],
        label: `Other: ${remaining} (${pct.toFixed(1)}%)`,
      });
    }

    const title = `Top ${top.length} values of ${total}`;

    return (
      <svg width={width} height={height} aria-hidden="true">
        <title>{title}</title>
        {segments.map((seg, i) => (
          <g key={i}>
            <rect x={seg.x} y={0} width={seg.width} height={height} fill={seg.color}>
              <title>{seg.label}</title>
            </rect>
          </g>
        ))}
      </svg>
    );
  }

  return null;
});
